import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import type { ThunkDispatch } from "redux-thunk";
import { DialogTitle } from "@mui/material";
import { Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { FormKit } from "components/forms/FormKit";
import { ResponsiveDialog } from "components/modal";
import { DialogFormWrapper } from "components/layout/forms/DialogFormWrapper";
import { AutoProgressButton } from "components/layout/forms/fields/AutoProgressButton";
import * as EventDirectorActions from "actions/eventDirector";
import { timezoneDate } from "utils/utils";
import { AnyAction } from "redux";
import { timeFormat } from "utils/dates";

dayjs.extend(utc);

interface EditBreakDialogProps {
    open: boolean;
    handleClose: () => void;
    adding?: boolean;
    eventId: string;
    initialValues?: { date?: string; index?: number };
    utcOffsetMinutes?: number;
    afterSubmit: () => void;
    afterDelete: () => void;
    breaks?: any[];
    [key: string]: any;
}

export const EditBreakDialog: React.FC<EditBreakDialogProps> = ({
    open,
    handleClose,
    adding,
    eventId,
    initialValues,
    utcOffsetMinutes,
    afterSubmit,
    afterDelete,
    breaks,
}) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();

    useEffect(() => {
        const index = initialValues?.index;
        if (index != null && breaks && !breaks[index]) {
            return handleClose();
        }
    }, [breaks, initialValues]);

    if (!open) return null;

    const title = adding ? "Add Break" : "Edit Break";

    const handleSubmit = async (values: {
        date: string;
        [key: string]: any;
    }) => {
        const date = dayjs(values.date).toISOString();
        const adjustedDate = timezoneDate(date, utcOffsetMinutes).toISOString();

        const submitData = {
            date: adjustedDate,
            index: initialValues?.index,
        };

        dispatch(EventDirectorActions.saveBreak(eventId, submitData));

        handleClose();
        afterSubmit();
    };

    const handleDelete = async () => {
        if (initialValues?.index !== undefined) {
            dispatch(
                EventDirectorActions.deleteBreak(eventId, initialValues.index),
            );

            handleClose();
            afterDelete();
        }
    };

    const secondaryAction = !adding && (
        <AutoProgressButton
            className="left"
            onClick={handleDelete}
            startIcon={<Delete />}
        >
            Delete
        </AutoProgressButton>
    );

    const fields = [
        {
            name: "date",
            type: "date",
            label: "Date & restart time:",
            required: true,
            hideClearButton: true,
            format: (value: string) => dayjs(value).toDate(),
            parse: (value: Date) => dayjs(value).toJSON(),
            inputProps: {
                showTimeSelect: true,
                autoFocus: true,
                timeIntervals: 5,
                inline: true,
                timeFormat: timeFormat(),
            },
        },
    ];

    return (
        <ResponsiveDialog
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-break-dialog-title"
        >
            <DialogTitle
                id="edit-break-dialog-title"
                className="sticky-dialog-title"
            >
                {title}
            </DialogTitle>
            <FormKit
                fields={fields}
                initialValues={initialValues || {}}
                onSubmit={handleSubmit}
                onCancel={handleClose}
                buttonLabel={adding ? "Save" : "Update"}
                enableOnPristine={adding}
                secondaryAction={secondaryAction}
                FormWrapperComponent={DialogFormWrapper}
            />
        </ResponsiveDialog>
    );
};
